/**
 * CloneCampaignTemplateDialog — TH-55110 (clone-and-customize).
 *
 * Modal disparado pela galeria `/campaigns/templates` quando o operador
 * clica em "Usar este template". Pede o nome da campanha antes de
 * confirmar o clone, permitindo customização imediata (o backend aceita
 * `name?` — se omitido herda o nome do template, mas a UX explícita é
 * mostrar o campo para evitar surpresa: o operador entende que está
 * criando uma instância dele, não apenas duplicando).
 *
 * O dialog é controlado pelo parent: recebe `template | null` (null =
 * fechado) e dispara `onConfirm({ name })` ao submit. Loading e erro
 * inline ficam controlados pelo parent também — mesma estratégia de
 * `NewCampaignDialog` (sem toast lib global).
 */

import { useEffect, useId, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { type CampaignTemplateRead } from "@/hooks/useCampaignTemplates";

export interface CloneCampaignTemplateDialogProps {
  /** Template a clonar. `null` mantém o dialog fechado. */
  template: CampaignTemplateRead | null;
  /** Estado de loading da mutation (controlado pelo parent). */
  isCloning: boolean;
  /** Mensagem de erro vinda da mutation; `null` esconde o alerta. */
  errorMessage: string | null;
  /** Fechar sem clonar (cancelar / overlay click). Ignorado durante isCloning. */
  onClose: () => void;
  /** Confirmar clone com o nome digitado. Parent dispara a mutation. */
  onConfirm: (payload: { name: string }) => void;
}

export function CloneCampaignTemplateDialog({
  template,
  isCloning,
  errorMessage,
  onClose,
  onConfirm,
}: CloneCampaignTemplateDialogProps) {
  const inputId = useId();
  const [name, setName] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Reset do form sempre que o template muda (incluindo abrir/fechar).
  // Não usar `template?.name ?? ""` direto no useState porque `useState`
  // só roda no mount — precisa reagir a `template` mudando.
  useEffect(() => {
    setName(template?.name ?? "");
    setLocalError(null);
  }, [template]);

  // Auto-focus do input ao abrir (após a animação curta do overlay).
  useEffect(() => {
    if (template && inputRef.current) {
      // Pequeno timeout para esperar o overlay; usar requestAnimationFrame
      // evita race com a animação do Radix Dialog.
      const id = requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
      return () => cancelAnimationFrame(id);
    }
    return undefined;
  }, [template]);

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (isCloning) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setLocalError("Informe um nome para a campanha.");
      return;
    }
    if (trimmed.length > 200) {
      setLocalError("Nome deve ter no máximo 200 caracteres.");
      return;
    }
    setLocalError(null);
    onConfirm({ name: trimmed });
  }

  const isOpen = template !== null;
  const visibleError = localError ?? errorMessage ?? null;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(next) => {
        if (!next && !isCloning) onClose();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogTitle>Usar template</DialogTitle>
        <DialogDescription>
          {template
            ? `Criar uma campanha a partir de "${template.name}". O conteúdo é clonado como rascunho — você ajusta os detalhes no editor.`
            : "Escolha um template."}
        </DialogDescription>

        <form
          className="mt-4 space-y-4"
          onSubmit={handleSubmit}
          data-testid="clone-template-form"
        >
          <div className="space-y-2">
            <label
              htmlFor={inputId}
              className="block text-xs font-medium text-[var(--color-foreground)]"
            >
              Nome da campanha
            </label>
            <Input
              id={inputId}
              ref={inputRef}
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (localError) setLocalError(null);
              }}
              disabled={isCloning}
              maxLength={200}
              placeholder="Ex.: Hortifrúti da Semana"
              aria-label="Nome da campanha"
              aria-invalid={visibleError ? true : undefined}
              data-testid="clone-template-name-input"
            />
            <p className="text-[11px] text-[var(--color-muted-foreground)]">
              Você pode renomear depois no editor.
            </p>
          </div>

          {visibleError && (
            <p
              role="alert"
              className="rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-xs text-[var(--color-destructive)]"
              data-testid="clone-template-error"
            >
              {visibleError}
            </p>
          )}

          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={isCloning}
              data-testid="clone-template-cancel"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isCloning}
              data-testid="clone-template-submit"
            >
              {isCloning ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2
                    className="h-4 w-4 animate-spin"
                    aria-hidden="true"
                  />
                  Criando campanha…
                </span>
              ) : (
                "Criar campanha"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
